import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/feature/Pagination";
import { movies } from "@/mocks/movies";
import { categories, regionsOptions, yearsOptions } from "@/mocks/categories";
import { filterMovies, isValidCategory, paginate, sortMovies, type SortKey } from "@/utils/movie";
import { useSeo } from "@/hooks/useSeo";

const PAGE_SIZE = 12;

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "createdAt", label: "最新上线" },
  { key: "popularity", label: "按热度" },
  { key: "rating", label: "按评分" },
];

export default function CategoryPage() {
  const { type = "" } = useParams();
  const valid = isValidCategory(type);
  const meta = categories.find((c) => c.key === type);

  const [region, setRegion] = useState("全部地区");
  const [year, setYear] = useState("全部年份");
  const [genre, setGenre] = useState("全部类型");
  const [sort, setSort] = useState<SortKey>("createdAt");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    setRegion("全部地区");
    setYear("全部年份");
    setGenre("全部类型");
    setSort("createdAt");
    setPage(1);
  }, [type]);

  const genreOptions = useMemo(
    () => ["全部类型", ...(meta ? meta.genres : [])],
    [meta],
  );

  const result = useMemo(() => {
    if (!valid) return [];
    const filtered = filterMovies(movies, {
      category: type as "movie" | "tv" | "variety",
      region,
      year,
      genre,
    });
    return sortMovies(filtered, sort);
  }, [valid, type, region, year, genre, sort]);

  const totalPages = Math.ceil(result.length / PAGE_SIZE);
  const pageItems = paginate(result, page, PAGE_SIZE);

  useSeo({
    title: `红桃视频在线观看 - ${meta ? meta.name : "影视"}影视专区`,
    description: meta
      ? `${meta.name}频道汇集丰富的${meta.name}资源，${meta.desc}`
      : "红桃视频在线观看分类影视专区，提供多维筛选与排序，帮助你快速找到心仪的影视作品。",
    keywords: meta
      ? `${meta.name},${meta.name}在线观看,红桃视频在线观看,影视资源`
      : "影视分类,红桃视频在线观看",
    canonicalPath: `/category/${type}`,
  });

  const updateFilter = (setter: (v: string) => void, value: string) => {
    setter(value);
    setPage(1);
  };

  const resetFilters = () => {
    setRegion("全部地区");
    setYear("全部年份");
    setGenre("全部类型");
    setSort("createdAt");
    setPage(1);
  };

  const chipClass = (active: boolean) =>
    `cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors md:text-sm ${
      active
        ? "bg-primary-500 text-background-50"
        : "border border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-600"
    }`;

  const filterGroup = (
    label: string,
    options: string[],
    value: string,
    setter: (v: string) => void,
  ) => (
    <div className="border-t border-background-200 py-4 first:border-t-0 first:pt-0">
      <div className="mb-3 text-sm font-medium text-foreground-800">{label}</div>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => updateFilter(setter, opt)}
            className={chipClass(value === opt)}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );

  if (!valid || !meta) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 py-24 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-50 text-primary-500">
          <i className="ri-emotion-sad-line text-3xl" />
        </span>
        <h1 className="mt-6 text-2xl font-bold text-foreground-950">未找到该分类</h1>
        <p className="mt-3 text-sm text-foreground-500">
          你访问的分类不存在，请从下方入口重新选择。
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          {categories.map((c) => (
            <Link
              key={c.key}
              to={`/category/${c.key}`}
              className="cursor-pointer rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              {c.name}
            </Link>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 py-8 md:px-8 md:py-10">
      {/* Breadcrumb */}
      <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500" aria-label="面包屑导航">
        <Link to="/" className="cursor-pointer transition-colors hover:text-primary-600">
          首页
        </Link>
        <span className="flex h-4 w-4 items-center justify-center">
          <i className="ri-arrow-right-s-line" />
        </span>
        <span>分类</span>
        <span className="flex h-4 w-4 items-center justify-center">
          <i className="ri-arrow-right-s-line" />
        </span>
        <span className="text-foreground-900">{meta.name}</span>
      </nav>

      <header className="mt-5">
        <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground-950 md:text-3xl">
          <span className="flex h-8 w-8 items-center justify-center text-primary-500">
            <i className={`${meta.icon} text-2xl`} />
          </span>
          {meta.name}影视专区
        </h1>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600">{meta.desc}</p>
      </header>

      <div className="mt-8 flex flex-col gap-8 lg:flex-row">
        {/* Filter sidebar */}
        <aside className="w-full lg:w-64 lg:shrink-0">
          <button
            type="button"
            onClick={() => setFilterOpen((v) => !v)}
            className="flex w-full cursor-pointer items-center justify-between rounded-lg border border-background-300 bg-background-100 px-4 py-3 text-sm font-medium text-foreground-800 lg:hidden"
          >
            <span className="flex items-center gap-2">
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-filter-3-line" />
              </span>
              筛选条件
            </span>
            <span className="flex h-4 w-4 items-center justify-center">
              <i className={filterOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
            </span>
          </button>

          <div
            className={`${
              filterOpen ? "block" : "hidden"
            } mt-3 rounded-lg border border-background-200 bg-background-100 p-4 lg:mt-0 lg:block`}
          >
            <div className="mb-2 hidden items-center justify-between lg:flex">
              <span className="flex items-center gap-2 text-sm font-bold text-foreground-950">
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-filter-3-line" />
                </span>
                筛选
              </span>
              <button
                type="button"
                onClick={resetFilters}
                className="cursor-pointer text-xs text-foreground-500 transition-colors hover:text-primary-600"
              >
                重置
              </button>
            </div>
            {filterGroup("地区", regionsOptions, region, (v) => updateFilter(setRegion, v))}
            {filterGroup("年份", yearsOptions, year, (v) => updateFilter(setYear, v))}
            {filterGroup("类型", genreOptions, genre, (v) => updateFilter(setGenre, v))}
          </div>
        </aside>

        {/* Content */}
        <div className="min-w-0 flex-1">
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-foreground-500">
              共找到 <span className="font-medium text-primary-600">{result.length}</span> 部{meta.name}
            </p>
            <div className="flex items-center gap-1 rounded-full border border-background-300 bg-background-100 px-1 py-1">
              {sortOptions.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => {
                    setSort(opt.key);
                    setPage(1);
                  }}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors md:text-sm ${
                    sort === opt.key
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-600 hover:text-primary-600"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {pageItems.length > 0 ? (
            <>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
                {pageItems.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
              <div className="mt-10">
                <Pagination currentPage={page} totalPages={totalPages} onChange={setPage} />
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 px-4 py-20 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                <i className="ri-search-eye-line text-3xl" />
              </span>
              <h3 className="mt-5 text-lg font-medium text-foreground-950">没有匹配的影视内容</h3>
              <p className="mt-2 max-w-md text-sm text-foreground-500">
                当前筛选条件下暂无结果，试试放宽条件或重置筛选。
              </p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-6 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                重置筛选条件
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}